<script setup lang="ts" generic="T extends Record<string, any> = Record<string, any>">
import type { ComponentPublicInstance } from 'vue'
import type {
  ConversationItem,
  ConversationItemUseOptions,
  ConversationMenuCommand,
  ConversationsEmits,
  GroupableOptions,
  GroupItem,
} from './types'
import { Delete, Edit, Loading, Top } from '@element-plus/icons-vue'
import { get } from 'radash'
import ConversationItemComponent from './components/item.vue'

const props = withDefaults(defineProps<{
  active?: string | number
  items: ConversationItem<T>[]
  itemsStyle?: Record<string, any>
  itemsHoverStyle?: Record<string, any>
  itemsActiveStyle?: Record<string, any>
  itemsMenuOpenedStyle?: Record<string, any>
  style?: Record<string, any>
  showTooltip?: boolean
  groupable?: boolean | GroupableOptions
  labelMaxWidth?: number
  labelHeight?: number
  showBuiltInMenu?: boolean
  showBuiltInMenuType?: 'hover' | 'always'
  menu?: any[]
  ungroupedTitle?: string
  tooltipPlacement?: string
  tooltipOffset?: number
  menuPlacement?: string
  menuOffset?: number
  menuShowArrow?: boolean
  menuClassName?: string
  menuTeleported?: boolean
  menuStyle?: Record<string, any>
  menuMaxHeight?: number
  loadMore?: () => void
  loadMoreLoading?: boolean
  showToTopBtn?: boolean
  showCheckbox?: boolean
  checkedIds?: string[]
  rowKey?: string
  labelKey?: string
}>(), {
  active: undefined,
  items: () => [],
  itemsStyle: () => ({}),
  itemsHoverStyle: () => ({}),
  itemsActiveStyle: () => ({}),
  itemsMenuOpenedStyle: () => ({}),
  style: () => ({}),
  showTooltip: false,
  groupable: false,
  labelMaxWidth: undefined,
  labelHeight: 20,
  showBuiltInMenu: false,
  showBuiltInMenuType: 'hover',
  menu: () => [
    { label: '重命名', key: 'rename', icon: markRaw(Edit), command: 'rename' },
    { label: '删除', key: 'delete', icon: markRaw(Delete), command: 'delete', menuItemHoverStyle: { color: 'red', backgroundColor: 'rgba(255, 0, 0, 0.1)' } },
  ],
  ungroupedTitle: '未分组',
  tooltipPlacement: 'top',
  tooltipOffset: 12,
  menuPlacement: 'bottom-start',
  menuOffset: 50,
  menuShowArrow: false,
  menuClassName: '',
  menuTeleported: true,
  menuStyle: () => ({}),
  menuMaxHeight: undefined,
  loadMore: undefined,
  loadMoreLoading: false,
  showToTopBtn: false,
  showCheckbox: false,
  checkedIds: () => [],
  rowKey: 'id',
  labelKey: 'label',
})

const emit = defineEmits<ConversationsEmits & {
  (e: 'toggleCheck', id: string, checked: boolean): void
}>()

const activeKey = computed({
  get: () => props.active,
  set: (v) => emit('update:active', v),
})

const alwaysShowBuiltInMenu = computed(() => props.showBuiltInMenuType === 'always' && props.showBuiltInMenu)

const itemsUse = computed<ConversationItemUseOptions<T>[]>(() =>
  props.items.map((item, index) => ({
    ...item,
    uniqueKey: props.rowKey ? (get(item, props.rowKey as string) as string) : index.toString(),
    label: (get(item, props.labelKey as string) || item.label || '') as string,
  })),
)

const filteredItems = computed(() => itemsUse.value)

const containerStyle = computed(() => ({
  '--elx-conversations-label-height': `${props.labelHeight}px`,
}))

function handleClick(item: ConversationItemUseOptions<T>) {
  if (item.disabled) return
  emit('change', item)
  activeKey.value = item.uniqueKey as string | number
}

const shouldUseGrouping = computed(() => !!props.groupable)

const groups = computed(() => {
  if (!shouldUseGrouping.value || !filteredItems.value.length) return []
  const groupMap: Record<string, GroupItem> = {}
  filteredItems.value.forEach((item) => {
    const groupName = item.group || props.ungroupedTitle
    if (!groupMap[groupName]) {
      groupMap[groupName] = { title: groupName, key: groupName, children: [], isUngrouped: !item.group }
    }
    groupMap[groupName].children.push(item)
  })
  const arr = Object.values(groupMap)
  if (typeof props.groupable === 'object' && props.groupable.sort) {
    return arr.sort((a, b) => {
      if (a.isUngrouped) return 1
      if (b.isUngrouped) return -1
      return (props.groupable as GroupableOptions).sort!(a.key, b.key)
    })
  }
  return arr.sort((a, b) => (a.isUngrouped ? 1 : b.isUngrouped ? -1 : 0))
})

const scrollbarRef = ref<any>(null)
const showScrollTop = ref(false)
const groupRefs = ref<Record<string, HTMLDivElement>>({})
const stickyGroupKeys = ref<Set<string>>(new Set())

function handleScroll(e: any) {
  showScrollTop.value = e.scrollTop > 200
  const wrap = scrollbarRef.value?.wrapRef
  if (!wrap) return
  if (wrap.scrollHeight - e.scrollTop - wrap.clientHeight < 20) {
    loadMoreData()
  }
  updateStickyStatus()
}

function updateStickyStatus() {
  if (!shouldUseGrouping.value || !groups.value.length) return
  stickyGroupKeys.value.clear()
  const scrollContainer = scrollbarRef.value?.wrapRef
  if (!scrollContainer) return
  if (groups.value.length === 1) {
    stickyGroupKeys.value.add(groups.value[0].key)
    return
  }
  const containerTop = scrollContainer.getBoundingClientRect().top
  const containerHeight = scrollContainer.clientHeight
  const isNearBottom = scrollContainer.scrollHeight - scrollContainer.scrollTop - containerHeight < 20
  if (isNearBottom && groups.value.length > 0) {
    stickyGroupKeys.value.add(groups.value[groups.value.length - 1].key)
    return
  }
  const visibleGroups: { group: GroupItem; relativeTop: number }[] = []
  for (const group of groups.value) {
    const el = groupRefs.value[group.key]
    if (el) {
      const rect = el.getBoundingClientRect()
      const relativeTop = rect.top - containerTop
      if (relativeTop < containerHeight && relativeTop + rect.height > 0) {
        visibleGroups.push({ group, relativeTop })
      }
    }
  }
  visibleGroups.sort((a, b) => a.relativeTop - b.relativeTop)
  if (visibleGroups.length > 0) {
    const fullyVisible = visibleGroups.find(g => g.relativeTop >= 0)
    stickyGroupKeys.value.add(fullyVisible ? fullyVisible.group.key : visibleGroups[0].group.key)
  } else if (groups.value.length > 0) {
    stickyGroupKeys.value.add(groups.value[0].key)
  }
}

function loadMoreData() { props.loadMore?.() }
function scrollToTop() { scrollbarRef.value?.setScrollTop(0) }

function handleMenuItemClick(command: ConversationMenuCommand, item: ConversationItem<T>) {
  emit('menuCommand', command, item)
}

function bindGroupRef(el: Element | ComponentPublicInstance | null, item: GroupItem) {
  if (el) groupRefs.value[item.key] = el as HTMLDivElement
}

onMounted(() => {
  if (shouldUseGrouping.value && groups.value.length > 0) {
    stickyGroupKeys.value.add(groups.value[0].key)
  }
})
</script>

<template>
  <div class="elx-conversations" :style="containerStyle">
    <slot name="header" />
    <ul class="elx-conversations__list" :style="{ height: '0', ...style }">
      <li class="elx-conversations__scroll-wrapper">
        <el-scrollbar ref="scrollbarRef" height="100%" class="elx-conversations__scrollbar" always @scroll="handleScroll">
          <div class="elx-conversations__scroll-content" style="min-height: 100%">
            <!-- Grouped mode -->
            <template v-if="shouldUseGrouping">
              <div v-for="group in groups" :key="group.key" :ref="(el: any) => bindGroupRef(el, group)" class="elx-conversations__group">
                <div
                  :class="[
                    'elx-conversations__group-title',
                    'elx-conversations__group-title--sticky',
                    stickyGroupKeys.has(group.key) ? 'elx-conversations__group-title--active-sticky' : '',
                  ]"
                >
                  <slot name="groupTitle" v-bind="{ group }">{{ group.title }}</slot>
                </div>
                <div class="elx-conversations__group-items">
                  <ConversationItemComponent
                    v-for="item in group.children"
                    :key="item.uniqueKey"
                    :item="item"
                    :active="item.uniqueKey === activeKey"
                    :items-style="itemsStyle"
                    :items-hover-style="itemsHoverStyle"
                    :items-active-style="itemsActiveStyle"
                    :items-menu-opened-style="itemsMenuOpenedStyle"
                    :prefix-icon="item.prefixIcon"
                    :show-tooltip="showTooltip"
                    :tooltip-placement="tooltipPlacement"
                    :tooltip-offset="tooltipOffset"
                    :suffix-icon="item.suffixIcon"
                    :label-max-width="labelMaxWidth"
                    :menu="(menu as any)"
                    :show-built-in-menu="showBuiltInMenu"
                    :always-show-built-in-menu="alwaysShowBuiltInMenu"
                    :menu-placement="menuPlacement"
                    :menu-offset="menuOffset"
                    :menu-max-height="menuMaxHeight"
                    :menu-style="menuStyle"
                    :menu-show-arrow="menuShowArrow"
                    :menu-class-name="menuClassName"
                    :menu-teleported="menuTeleported"
                    :show-checkbox="showCheckbox"
                    :checked="checkedIds.includes(String(item.uniqueKey))"
                    @click="handleClick(item)"
                    @menu-command="handleMenuItemClick"
                    @toggle-check="(id, v) => emit('toggleCheck', id, v)"
                  >
                    <template v-if="$slots.label" #label>
                      <slot name="label" v-bind="{ item }" />
                    </template>
                    <template v-if="$slots['more-filled']" #more-filled="props">
                      <slot name="more-filled" v-bind="props" />
                    </template>
                    <template v-if="$slots.menu" #menu="props">
                      <slot name="menu" v-bind="props" />
                    </template>
                    <template v-if="$slots.default" #default="props">
                      <slot name="default" v-bind="props" />
                    </template>
                  </ConversationItemComponent>
                </div>
              </div>
            </template>

            <!-- Flat mode -->
            <template v-else>
              <ConversationItemComponent
                v-for="item in filteredItems"
                :key="item.uniqueKey"
                :item="item"
                :active="item.uniqueKey === activeKey"
                :items-style="itemsStyle"
                :items-hover-style="itemsHoverStyle"
                :items-active-style="itemsActiveStyle"
                :items-menu-opened-style="itemsMenuOpenedStyle"
                :prefix-icon="item.prefixIcon"
                :show-tooltip="showTooltip"
                :tooltip-placement="tooltipPlacement"
                :tooltip-offset="tooltipOffset"
                :suffix-icon="item.suffixIcon"
                :label-max-width="labelMaxWidth"
                :menu="(menu as any)"
                :show-built-in-menu="showBuiltInMenu"
                :always-show-built-in-menu="alwaysShowBuiltInMenu"
                :menu-placement="menuPlacement"
                :menu-offset="menuOffset"
                :menu-max-height="menuMaxHeight"
                :menu-style="menuStyle"
                :menu-show-arrow="menuShowArrow"
                :menu-class-name="menuClassName"
                :menu-teleported="menuTeleported"
                :show-checkbox="showCheckbox"
                :checked="checkedIds.includes(String(item.uniqueKey))"
                @click="handleClick(item)"
                @menu-command="handleMenuItemClick"
                @toggle-check="(id, v) => emit('toggleCheck', id, v)"
              >
                <template v-if="$slots.label" #label>
                  <slot name="label" v-bind="{ item }" />
                </template>
                <template v-if="$slots['more-filled']" #more-filled="props">
                  <slot name="more-filled" v-bind="props" />
                </template>
                <template v-if="$slots.menu" #menu="props">
                  <slot name="menu" v-bind="props" />
                </template>
                <template v-if="$slots.default" #default="props">
                  <slot name="default" v-bind="props" />
                </template>
              </ConversationItemComponent>
            </template>
          </div>

          <div v-if="loadMoreLoading" class="elx-conversations__load-more">
            <slot name="load-more">
              <el-icon class="elx-conversations__load-more-icon"><Loading /></el-icon>
              <span>加载更多...</span>
            </slot>
          </div>
        </el-scrollbar>
      </li>
    </ul>
    <slot name="footer" />
    <el-button v-show="showScrollTop && showToTopBtn" class="elx-conversations__to-top-btn" circle @click="scrollToTop">
      <el-icon><Top /></el-icon>
    </el-button>
  </div>
</template>

<style scoped lang="scss">
@use './style.scss';
</style>
